/** Единая подписка IEC+ (план §4.8). Цены только в USD, minor units (центы). */

export const IEC_PLUS_TIERS = ["free", "plus", "pro"] as const;
export type IecPlusTier = (typeof IEC_PLUS_TIERS)[number];

export const IEC_PLUS_PRICES = {
	plus: { monthlyMinor: 899, yearlyMinor: 7900 },
	pro: { monthlyMinor: 1999, yearlyMinor: 17_900 },
} as const;

/**
 * Пробный период — **14 дней** (решение владельца 22.08.2026, было 7).
 *
 * Это и потолок заявки сервиса, и значение по умолчанию: сервис вправе
 * попросить меньше, больше — зажимается. Хранится потолок ещё и в политике
 * (`service_trial_policies.max_days`), чтобы оператор мог сузить его для
 * конкретной области, не трогая код; константа задаёт умолчание новой строки.
 */
export const TRIAL_MAX_DAYS = 14;

/**
 * Область экосистемного пробного. Тот же сентинел, что в ключе идемпотентности
 * подарка (`entitlements.gift:<sub>:<serviceId|*>`) — второго не заводим.
 */
export const TRIAL_SCOPE_ECOSYSTEM = "*";

/**
 * Прежнее имя константы. Оставлено ссылкой, а не вторым литералом: копия
 * сходится ровно до первого расхождения (грабли §27).
 */
export const IEC_PLUS_TRIAL_DAYS = TRIAL_MAX_DAYS;

/**
 * Ключ тарифа «уровень + период». Им адресуются планы PayPal
 * (`PAYPAL_PLAN_PLUS_MONTHLY` и т.д.) и он же — часть идентификатора цели гранта.
 */
export const IEC_PLUS_PLAN_KEYS = [
	"plus_monthly",
	"plus_yearly",
	"pro_monthly",
	"pro_yearly",
] as const;
export type IecPlusPlanKey = (typeof IEC_PLUS_PLAN_KEYS)[number];

export function planKeyOf(tier: "plus" | "pro", period: "monthly" | "yearly"): IecPlusPlanKey {
	return `${tier}_${period}`;
}

/**
 * Идентификатор цели гранта Баллов под подписку — ЕДИНЫЙ для всей экосистемы.
 *
 * Грант выпускается ДО checkout'а и квотируется под КОНКРЕТНУЮ цену, поэтому он
 * привязан к конкретному тарифу: грант на `plus_monthly` нельзя предъявить к
 * счёту за `pro_yearly` — там другая сумма и другой потолок 50%. Проверку делает
 * `billing` (`checkout-amount.ts`), а формат обязан быть один и здесь, иначе
 * витрина выпустит грант, который биллинг не примет.
 *
 * `serviceId` пуст — экосистемный IEC+; иначе подписка на один сервис.
 */
export function subscriptionGrantTargetId(input: {
	readonly tier: "plus" | "pro";
	readonly period: "monthly" | "yearly";
	readonly serviceId?: string | null;
}): string {
	const scope = input.serviceId ?? "iec-plus";
	return `${scope}:${planKeyOf(input.tier, input.period)}`;
}

/** Подарок участникам программ ЦМО — IEC+ Pro на 12 месяцев. */
export const PROGRAMME_GIFT = {
	tier: "pro",
	months: 12,
} as const;

/** Источник права. Сервис берёт максимум из локальной подписки и entitlement. */
export const ENTITLEMENT_SOURCES = ["subscription", "programme", "grant", "trial"] as const;
export type EntitlementSource = (typeof ENTITLEMENT_SOURCES)[number];
